'use client';

import React from 'react';
import type { ChatSummary } from '../types/index.js';
import { MessageSquare, Clock, Hash, Archive } from 'lucide-react';
import { useChatId } from '../contexts/index.js';
import { WRAPPER, SCROLL } from '../styles/chat-layout.js';

interface ChatSidebarProps {
  chats: ChatSummary[];
  selectedChatId: string | null;
  onSelectChat: (chatId: string) => void;
  onCreateChat: () => void;
  isLoading: boolean;
  locale?: string;
  meridianLogoUrl?: string;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const truncateTitle = (title: string, maxLength: number = 35): string => {
  return title.length > maxLength ? title.substring(0, maxLength) + '...' : title;
};

export function ChatSidebar({
  chats,
  selectedChatId,
  onSelectChat,
  onCreateChat,
  isLoading,
  locale = 'en',
  meridianLogoUrl = '/meridian.png',
}: ChatSidebarProps) {
  const { setChatId } = useChatId();

  const handleChatHistoryClick = () => {
    // Extensions do not have Next.js routing. Clearing selection returns the UI to
    // the "start screen" within the current surface.
    setChatId(null);
  };

  return (
    <div className="w-80 border-r border-gray-200 dark:border-gray-700 flex flex-col bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0">
        <div className="mb-4">
          <button
            onClick={handleChatHistoryClick}
            className="flex items-center gap-3 mb-1 hover:opacity-80 transition-opacity cursor-pointer w-full"
            title="Go to chat history"
          >
            <img
              src={meridianLogoUrl}
              alt="Meridian"
              className="w-6 h-6 rounded-full object-cover"
            />
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              Chat History
            </h2>
          </button>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Your Conversations with Meridian
          </p>
        </div>
        <button
          onClick={onCreateChat}
          className="w-full px-4 py-3 bg-gradient-to-r from-primary to-primary text-white rounded-lg hover:from-primary-light hover:to-primary-light font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
        >
          <span>+</span>
          <span>New Chat</span>
        </button>
      </div>

      {/* Chat List */}
      <div className={WRAPPER.chatSidebarContainer}>
        {chats.length === 0 ? (
          <div className="p-6 text-center">
            <div className="flex justify-center mb-3">
              <MessageSquare className="w-12 h-12 text-gray-400 dark:text-gray-500" />
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              No chats yet
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              Start a new conversation
            </p>
          </div>
        ) : (
          <div className="space-y-2 p-3 pb-[150px]">
            {chats.map((chat) => {
              const isSelected = selectedChatId === chat.id;
              const isArchived = chat.status === 'archived';
              
              return (
                <button
                  key={chat.id}
                  onClick={() => setChatId(chat.id)}
                  className={`w-full text-left rounded-lg transition-all duration-200 group overflow-hidden border ${
                    isSelected
                      ? 'bg-gradient-to-r from-brand-accent/15 to-brand-light/15 dark:from-brand-accent/30 dark:to-brand-light/30 border-brand-accent/60 dark:border-brand-accent/80 shadow-md'
                      : 'bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 hover:border-brand-accent/40 dark:hover:border-brand-accent/50 hover:shadow-sm'
                  }`}
                  title={chat.title}
                >
                  <div className="p-3">
                    {/* Title and Status */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className={`font-semibold text-sm flex-1 leading-tight ${
                        isSelected
                          ? 'text-brand-teal-dark dark:text-white'
                          : 'text-slate-800 dark:text-slate-100 group-hover:text-brand-teal-dark dark:group-hover:text-brand-light'
                      }`}>
                        {truncateTitle(chat.title)}
                      </h3>
                      {isArchived && (
                        <Archive className="w-4 h-4 text-slate-400 dark:text-slate-500 flex-shrink-0 mt-0.5" />
                      )}
                    </div>

                    {/* Metadata Row */}
                    <div className="flex items-center gap-3 text-xs">
                      {/* Message Count */}
                      <div className="flex items-center gap-1 text-slate-600 dark:text-slate-400">
                        <Hash className="w-3.5 h-3.5" />
                        <span>{chat.messageCount}</span>
                      </div>

                      {/* Last Message Time */}
                      <div className="flex items-center gap-1 text-slate-600 dark:text-slate-400">
                        <Clock className="w-3.5 h-3.5" />
                        <span>{formatDate(chat.lastMessageAt)}</span>
                      </div>
                    </div>

                    {/* Created Date */}
                    <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                      Created {formatDate(chat.createdAt)}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

